import React, { useMemo } from 'react'
import { Box, Button, Typography, makeStyles, Theme } from '@material-ui/core'

import {
    useOccupancyClearSuggestions,
    useOccupancySuggest,
} from '../../../hooks/useRenewalOffers'
import {
    OccupancyBoardFilter,
    OccupancyFilter,
    OccupancyLease,
    OccupancyUnit,
} from './types'
import {
    matchScoreColor,
    occupancyChromeStats,
} from './occupancyMatchScoreUtils'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: theme.spacing(1.5),
    },
    tiles: {
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'flex-start',
        gap: theme.spacing(1),
        flex: '1 1 420px',
    },
    tile: {
        minWidth: 112,
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        textAlign: 'left',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
    },
    tileButton: {
        cursor: 'pointer',
        '&:hover': {
            borderColor: theme.palette.primary.light,
        },
    },
    tileActive: {
        borderColor: theme.palette.primary.main,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
        backgroundColor:
            theme.palette.type === 'dark'
                ? 'rgba(25, 118, 210, 0.12)'
                : 'rgba(25, 118, 210, 0.06)',
    },
    tileValue: {
        fontSize: '1.25rem',
        fontWeight: 700,
        fontFamily: 'monospace',
        lineHeight: 1.2,
        color: theme.palette.text.primary,
    },
    tileLabel: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    tileHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        gap: theme.spacing(1),
    },
}))

type ChromeTileId = 'unassigned' | 'drafts' | 'openBeds' | 'conflicts' | 'min'

function formatScore(value: number | null, digits = 0) {
    if (value == null) return '—'
    if (digits === 0) return String(Math.round(value))
    return value.toFixed(digits)
}

function scoredHint(count: number) {
    if (count === 0) return 'No scored units yet'
    return `Across ${count} unit${count === 1 ? '' : 's'}`
}

function OccupancyStatTile({
    label,
    value,
    hint,
    valueColor,
    active,
    onClick,
}: {
    label: string
    value: string | number
    hint: string
    valueColor?: string
    active?: boolean
    onClick?: () => void
}) {
    const classes = useStyles()
    const clickable = onClick != null
    return (
        <Box
            className={`${classes.tile} ${
                clickable ? classes.tileButton : ''
            } ${active ? classes.tileActive : ''}`}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={
                clickable
                    ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              onClick()
                          }
                      }
                    : undefined
            }
        >
            <Typography
                className={classes.tileValue}
                style={valueColor ? { color: valueColor } : undefined}
            >
                {value}
            </Typography>
            <Typography className={classes.tileLabel}>{label}</Typography>
            <Typography className={classes.tileHint}>{hint}</Typography>
        </Box>
    )
}

export function OccupancyChrome({
    leases,
    units,
    viewMode,
    leaseFilter,
    boardFilter,
    onUnassigned,
    onDrafts,
    onOpenBeds,
    onConflicts,
    onMinScore,
    onSuggested,
}: {
    leases: OccupancyLease[]
    units: OccupancyUnit[]
    viewMode: 'leases' | 'bed_spaces'
    leaseFilter: OccupancyFilter
    boardFilter: OccupancyBoardFilter
    onUnassigned: () => void
    onDrafts: () => void
    onOpenBeds: () => void
    onConflicts: () => void
    onMinScore: (unitId: number) => void
    onSuggested?: () => void
}) {
    const classes = useStyles()
    const suggest = useOccupancySuggest()
    const clearSuggestions = useOccupancyClearSuggestions()
    const stats = useMemo(() => occupancyChromeStats(units, leases), [
        units,
        leases,
    ])

    const activeTile: ChromeTileId | null =
        viewMode === 'leases' && leaseFilter === 'unassigned'
            ? 'unassigned'
            : viewMode === 'bed_spaces' && boardFilter === 'drafts'
            ? 'drafts'
            : viewMode === 'bed_spaces' && boardFilter === 'incomplete'
            ? 'openBeds'
            : viewMode === 'bed_spaces' && boardFilter === 'conflicts'
            ? 'conflicts'
            : null

    const minColor = matchScoreColor({
        kind: stats.minKind ?? 'none',
        score: stats.minScore,
        label: '',
        reason: '',
        tooltip: '',
    })

    return (
        <Box className={classes.root}>
            <Box className={classes.tiles}>
                <OccupancyStatTile
                    label="Unassigned"
                    value={stats.unassigned}
                    hint="People waiting"
                    active={activeTile === 'unassigned'}
                    onClick={onUnassigned}
                />
                <OccupancyStatTile
                    label="Drafts pending"
                    value={stats.drafts}
                    hint="Need confirm"
                    active={activeTile === 'drafts'}
                    onClick={onDrafts}
                />
                <OccupancyStatTile
                    label="Open beds"
                    value={stats.openBeds}
                    hint="Vacant spaces"
                    active={activeTile === 'openBeds'}
                    onClick={onOpenBeds}
                />
                <OccupancyStatTile
                    label="Conflicts"
                    value={stats.conflicts}
                    hint="Must-have clash"
                    active={activeTile === 'conflicts'}
                    onClick={onConflicts}
                />
                <OccupancyStatTile
                    label="Min score"
                    value={formatScore(stats.minScore)}
                    hint={stats.minScore == null ? scoredHint(0) : 'Worst unit'}
                    valueColor={minColor}
                    onClick={
                        stats.minUnitId != null
                            ? () => onMinScore(stats.minUnitId!)
                            : undefined
                    }
                />
                <OccupancyStatTile
                    label="Average score"
                    value={formatScore(stats.averageScore, 1)}
                    hint={scoredHint(stats.scoredCount)}
                />
                <OccupancyStatTile
                    label="Median score"
                    value={formatScore(stats.medianScore, 1)}
                    hint={
                        stats.medianScore == null
                            ? scoredHint(0)
                            : 'Typical unit'
                    }
                />
            </Box>
            <Box className={classes.actions}>
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={suggest.isLoading}
                    onClick={() =>
                        suggest.mutate(
                            {},
                            {
                                onSuccess: () => onSuggested?.(),
                            },
                        )
                    }
                    style={{ textTransform: 'none', fontWeight: 600 }}
                >
                    {suggest.isLoading ? 'Suggesting…' : 'Suggest roommates'}
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={clearSuggestions.isLoading || stats.drafts === 0}
                    onClick={() => clearSuggestions.mutate(undefined)}
                    style={{ textTransform: 'none', fontWeight: 600 }}
                >
                    {clearSuggestions.isLoading
                        ? 'Clearing…'
                        : `Clear drafts (${stats.drafts})`}
                </Button>
            </Box>
        </Box>
    )
}
